'use client';

import { DATA_GRID_MODE } from '@/constants/dataGrid';
import { type RefObject, useEffect, useMemo, useRef } from 'react';
import type {
  DataGridProProps,
  GridApiPro,
  GridCallbackDetails,
  GridColumnGroupingModel,
  GridColumnNode,
} from '@mui/x-data-grid-pro';
import { DataGridPro, gridClasses, useGridApiRef } from '@mui/x-data-grid-pro';
import { alpha } from '@mui/material/styles';
import type { SystemStyleObject, Theme } from '@mui/system';
import { blend } from '@mui/system/colorManipulator';
import { Box, CircularProgress } from '@mui/material';
import { blue, purple, slate, white } from '@/lib/colours';
import {
  categoryBorderColor,
  categoryCellBackground,
  categoryCellHoverBackground,
  categoryHeaderBackground,
  EBV_CATEGORY,
  EBV_BREED_AVERAGE_ROW_HEIGHT,
  BREED_AVERAGE_DUAL_LINE_FLAG,
  EBV_COLUMN_HEADERS_TOTAL_HEIGHT,
  EBV_GROUP_COLUMN_HEADER_HEIGHT,
  EBV_TRAIT_COLUMN_HEADER_HEIGHT,
  ebvGroupingOptionsFromDisplay,
  getCategoryTint,
  getEffectiveCategoryColorMap,
  getEbvCategoryClassSuffix,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import { Pagination } from '@/primitives/Pagination';
import { PAGE_SIZE_OPTIONS } from '../constants';
import useGridStore from '@/publicSearch/store/useGridStore';

// Custom loading overlay - just shows a centered spinner, no skeleton
function CustomLoadingOverlay() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        width: '100%',
      }}
    >
      <CircularProgress size={40} />
    </Box>
  );
}

/** Collect every leaf `field` referenced anywhere in a column grouping model. */
function collectGroupedFields(
  nodes: readonly GridColumnNode[],
  acc: Set<string>,
): void {
  for (const node of nodes) {
    if ('field' in node) {
      acc.add(node.field);
    } else if (node.children) {
      collectGroupedFields(node.children, acc);
    }
  }
}

/**
 * True when the grouping model has at least one grouped column that exists in
 * `columns` and is not hidden by `columnVisibilityModel`. Used to decide whether
 * the tall two-row (group + trait) EBV header should be applied; an EBV society
 * always supplies a grouping model even with every EBV column toggled off, so
 * presence of the model alone must not drive the header height.
 */
function hasVisibleGroupedColumns(
  columnGroupingModel: GridColumnGroupingModel | undefined,
  columns: readonly { field: string }[] | undefined,
  columnVisibilityModel: Record<string, boolean> | undefined,
): boolean {
  if (!columnGroupingModel?.length || !columns?.length) return false;
  const groupedFields = new Set<string>();
  collectGroupedFields(columnGroupingModel, groupedFields);
  if (groupedFields.size === 0) return false;
  const visibility = columnVisibilityModel ?? {};
  return columns.some(
    (column) =>
      groupedFields.has(column.field) && visibility[column.field] !== false,
  );
}

export const searchApiRef = {
  current: null,
} as RefObject<GridApiPro | null>;

export type PublicSearchDataGridProps = DataGridProProps & {
  columnHeaderHeight?: number;
  rowHeight?: number;
};

// Default options for any external `apiRef.autosizeColumns()` call that
// doesn't pass its own options (e.g. MUI's built-in "Resize column" menu
// item). Internal effects no longer call autosize — see the long comment
// further down — but this default keeps user-driven autosize behaviour
// consistent with how `SearchHeader`, `TraitSettingsContext` and
// `ProgenyTable` invoke it explicitly.
const AUTOSIZE_OPTIONS = {
  includeHeaders: true,
  includeOutliers: true,
  expand: true,
} as const;

type DataGridSx = DataGridProProps['sx'];
type DataGridSxArray = Extract<NonNullable<DataGridSx>, readonly unknown[]>;

/** Normalize an optional DataGrid `sx` prop into an array so it can be spread into a combined `sx` array. */
function toDataGridSxArray(sx: DataGridSx): DataGridSxArray {
  if (Array.isArray(sx)) return sx;
  return (sx ? [sx] : []) as DataGridSxArray;
}

export default function PublicSearchDataGrid(props: PublicSearchDataGridProps) {
  const {
    columnHeaderHeight = 48,
    rowHeight = 44,
    sx: sxFromProps,
    columnGroupingModel,
    // BLS-396: deliberately NOT forwarded to DataGridPro — see
    // handlePaginationModelChange below.
    onPaginationModelChange,
    ...dataGridProps
  } = props;
  // EBV-enabled societies always supply a `columnGroupingModel` (one group per
  // EBV category), even when every EBV column is toggled off — which is the
  // default. If we keyed the tall two-row EBV header purely off the model being
  // present, the grid would reserve the full grouped-header height (group row +
  // trait row) while rendering no group headers at all, leaving a big empty gap
  // above the normal column headers. Only treat the grouping as active when at
  // least one grouped column is actually present AND visible.
  const hasEbvColumnGrouping = useMemo(
    () =>
      hasVisibleGroupedColumns(
        columnGroupingModel,
        dataGridProps.columns,
        dataGridProps.columnVisibilityModel,
      ),
    [
      columnGroupingModel,
      dataGridProps.columns,
      dataGridProps.columnVisibilityModel,
    ],
  );
  const effectiveColumnHeaderHeight = hasEbvColumnGrouping
    ? EBV_TRAIT_COLUMN_HEADER_HEIGHT
    : columnHeaderHeight;
  const effectiveColumnGroupHeaderHeight = hasEbvColumnGrouping
    ? EBV_GROUP_COLUMN_HEADER_HEIGHT
    : columnHeaderHeight;
  const apiRef = useGridApiRef();
  const { useColourSlotGrouping } = useEbvDisplayOptions();
  const categoryColorMap = useMemo(
    () =>
      getEffectiveCategoryColorMap(
        ebvGroupingOptionsFromDisplay({ useColourSlotGrouping }),
      ),
    [useColourSlotGrouping],
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollPosition, setScrollPosition } = useGridStore();
  const restoredScrollRef = useRef<boolean>(false);
  const scrollRafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!apiRef.current?.showPreferences) return;
    searchApiRef.current = apiRef.current;
  }, [apiRef]);

  // Columns use flex + minWidth, so they reflow on resize without a manual
  // autosize here — the old autosize-on-prop-change effect caused a
  // load-then-resnap flicker. This effect only recomputes MUI's viewport
  // dimensions when the container becomes visible (e.g. tab swap); content-fit
  // snapshots are left to explicit callers via `searchApiRef.current.autosizeColumns(...)`.
  useEffect(() => {
    if (!containerRef.current) return;

    const updateGridDimensions = () => {
      if (!apiRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      if (rect.height <= 0 || rect.width <= 0) return;
      // updateDimensions is private API but is the only way to nudge the
      // virtualizer when the container size changes mid-mount.
      (
        apiRef.current as { updateDimensions?: () => void } | null
      )?.updateDimensions?.();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0) {
            updateGridDimensions();
          }
        });
      },
      { threshold: [0, 0.01, 0.1] },
    );

    observer.observe(containerRef.current);
    window.addEventListener('resize', updateGridDimensions);
    updateGridDimensions();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateGridDimensions);
    };
  }, [apiRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const scroller = container.querySelector(
      '.MuiDataGrid-virtualScroller',
    ) as HTMLElement | null;
    if (!scroller) return;

    if (!restoredScrollRef.current && scrollPosition > 0) {
      scroller.scrollTop = scrollPosition;
      restoredScrollRef.current = true;
    }

    const handleScroll = () => {
      if (scrollRafRef.current !== null) return;
      scrollRafRef.current = window.requestAnimationFrame(() => {
        setScrollPosition(scroller.scrollTop);
        scrollRafRef.current = null;
      });
    };

    scroller.addEventListener('scroll', handleScroll);
    return () => {
      scroller.removeEventListener('scroll', handleScroll);
      if (scrollRafRef.current !== null) {
        window.cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [scrollPosition, setScrollPosition]);

  useEffect(() => {
    restoredScrollRef.current = false;
  }, [dataGridProps.rows]);

  const paginationModel = dataGridProps.paginationModel;
  const rowCount =
    typeof dataGridProps.rowCount === 'number' ? dataGridProps.rowCount : 0;
  const showPagination = Boolean(dataGridProps.pagination && paginationModel);

  // BLS-396: report page changes straight to the caller instead of routing them
  // through the grid. DataGrid clamps the page to 0 while it still believes the
  // row count is 0 — every mount, before the first response — and delivers that
  // clamp through a debounce, so it used to land *after* the real row count and
  // knock the user back to page 1. The grid stays fully controlled by the
  // `paginationModel` prop, so its own state can no longer reach the store.
  const handlePaginationModelChange = (
    model: NonNullable<DataGridProProps['paginationModel']>,
  ) => {
    if (onPaginationModelChange) {
      onPaginationModelChange(model, {
        reason: 'setPaginationModel',
        api: apiRef.current,
      } as unknown as GridCallbackDetails<'pagination'>);
      return;
    }

    apiRef.current?.setPaginationModel(model);
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        minHeight: '400px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <DataGridPro
        {...dataGridProps}
        columnGroupingModel={
          hasEbvColumnGrouping ? columnGroupingModel : undefined
        }
        apiRef={apiRef}
        autosizeOnMount={false}
        columnHeaderHeight={effectiveColumnHeaderHeight}
        columnGroupHeaderHeight={effectiveColumnGroupHeaderHeight}
        disableColumnFilter
        hideFooter
        autosizeOptions={AUTOSIZE_OPTIONS}
        disableRowSelectionOnClick
        getRowHeight={(params) => {
          if (params.id === -1) {
            const dualLine = Boolean(
              (params.model as Record<string, unknown>)?.[
                BREED_AVERAGE_DUAL_LINE_FLAG
              ],
            );
            return dualLine ? EBV_BREED_AVERAGE_ROW_HEIGHT : rowHeight;
          }
          const parentGetRowHeight = dataGridProps.getRowHeight;
          if (typeof parentGetRowHeight === 'function') {
            return parentGetRowHeight(params);
          }
          if (typeof parentGetRowHeight === 'number') {
            return parentGetRowHeight;
          }
          return rowHeight;
        }}
        getRowClassName={(params) => {
          // Special styling for EBV average row (ID is -1)
          if (params.id === -1) {
            return 'ebv-average-row';
          }
          return params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
        }}
        isRowSelectable={(params) => params.id !== -1}
        paginationMode={DATA_GRID_MODE.SERVER}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        slots={{
          ...dataGridProps.slots,
          loadingOverlay: CustomLoadingOverlay,
        }}
        slotProps={{
          ...dataGridProps.slotProps,
          loadingOverlay: {
            variant: 'circular-progress',
          },
        }}
        sortingMode={DATA_GRID_MODE.SERVER}
        rowHeight={rowHeight}
        sx={[
          (theme: Theme): SystemStyleObject<Theme> => ({
            border: 'none',
            paddingBlock: 0.5,
            flex: 1,
            minHeight: 0,
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto',
            },

            [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
              {
                outline: 'none',
              },
            '&.MuiDataGrid-root': {
              borderWidth: '0px',
              minHeight: '200px',
              height: '100%',

              '&::before': {
                content: '""',
                position: 'absolute',
                height: '10px',
                width: '100%',
                boxShadow: `0 0 8px ${alpha(theme.palette.common.black, 0.12)}`,
                pointerEvents: 'none',
              },
            },
            '& .MuiDataGrid-overlayWrapperInner': {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            },
            '.MuiDataGrid-columnHeaderTitleContainerContent': {
              fontSize: '12px',
              fontWeight: '400',
              whiteSpace: 'normal',
              lineHeight: 1.3,
            },
            '.MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1.3,
            },
            '.MuiDataGrid-columnHeader.pl': {
              paddingLeft: '14px',
            },
            '.MuiDataGrid-columnHeader': {
              ...(hasEbvColumnGrouping
                ? {}
                : {
                    minHeight: `${columnHeaderHeight}px`,
                    height: `${columnHeaderHeight}px`,
                  }),
              paddingTop: '4px',
              paddingBottom: '4px',
              paddingLeft: '10px',
              paddingRight: '10px',
            },
            '& .MuiDataGrid-columnHeader.ebv-column': {
              overflow: 'visible',
              [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                position: 'relative',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              },
              // Sort/filter icons sit in a flex sibling that expands on hover and
              // shifts centered EBV labels. Pin them over the header instead.
              [`& .${gridClasses.iconButtonContainer}`]: {
                position: 'absolute',
                left: 0,
                top: 0,
                width: 'auto !important',
                margin: 0,
                zIndex: 2,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                textAlign: 'center',
                overflow: 'visible',
              },
              '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                width: '100%',
                overflow: 'visible',
              },
            },
            '& .MuiDataGrid-columnHeader.ebv-row-labels-header': {
              paddingRight: '6px',
            },
            '& .MuiDataGrid-cell.ebv-row-labels-cell': {
              paddingRight: '6px',
              paddingLeft: '4px',
            },
            // Highlight EBV columns with display === 2: solid purple header,
            // 3px purple outline (header top + sides, cell sides), subtle
            // lavender cell fill (same blend as other EBV categories), and a
            // single continuous elevation shadow on the left edge of the index
            // block so it reads as raised.
            '.MuiDataGrid-columnHeader.ebv-highlighted': {
              backgroundColor: purple[300],
              borderTop: `3px solid ${purple[300]}`,
              borderLeft: `3px solid ${purple[300]}`,
              borderRight: `3px solid ${purple[300]}`,
              boxSizing: 'border-box',
              position: 'relative',
              zIndex: 2,
              overflow: 'visible',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: '6px',
                transform: 'translateX(-100%)',
                pointerEvents: 'none',
                background: `linear-gradient(to left, ${alpha(theme.palette.common.black, 0.25)}, transparent)`,
              },
              ...theme.applyStyles('dark', {
                backgroundColor: alpha(purple[300], 0.25),
                borderTopColor: alpha(purple[300], 0.5),
                borderLeftColor: alpha(purple[300], 0.5),
                borderRightColor: alpha(purple[300], 0.5),
                '&::before': {
                  background: `linear-gradient(to left, ${alpha(theme.palette.common.black, 0.55)}, transparent)`,
                },
              }),
            },
            '.MuiDataGrid-cell.ebv-highlighted': {
              backgroundColor: categoryCellBackground(
                getCategoryTint(EBV_CATEGORY.INDEXES),
                white[50],
                false,
              ),
              borderLeft: `3px solid ${purple[300]}`,
              borderRight: `3px solid ${purple[300]}`,
              boxSizing: 'border-box',
              position: 'relative',
              zIndex: 1,
              overflow: 'visible',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: '6px',
                transform: 'translateX(-100%)',
                pointerEvents: 'none',
                background: `linear-gradient(to left, ${alpha(theme.palette.common.black, 0.25)}, transparent)`,
              },
              ...theme.applyStyles('dark', {
                backgroundColor: categoryCellBackground(
                  getCategoryTint(EBV_CATEGORY.INDEXES),
                  slate[800],
                  true,
                ),
                borderLeftColor: alpha(purple[300], 0.5),
                borderRightColor: alpha(purple[300], 0.5),
                '&::before': {
                  background: `linear-gradient(to left, ${alpha(theme.palette.common.black, 0.55)}, transparent)`,
                },
              }),
            },
            [`& .${gridClasses.row}:hover .MuiDataGrid-cell.ebv-highlighted`]: {
              backgroundColor: categoryCellHoverBackground(
                getCategoryTint(EBV_CATEGORY.INDEXES),
                white[50],
                theme.palette.action.hover,
                false,
              ),
              ...theme.applyStyles('dark', {
                backgroundColor: categoryCellHoverBackground(
                  getCategoryTint(EBV_CATEGORY.INDEXES),
                  slate[800],
                  theme.palette.action.hover,
                  true,
                ),
              }),
            },
            // Avoid doubled border and keep the elevation shadow only on the
            // leftmost index column (drop both on any index column that
            // immediately follows another).
            '.MuiDataGrid-columnHeader.ebv-highlighted + .MuiDataGrid-columnHeader.ebv-highlighted':
              {
                borderLeftWidth: 0,
                '&::before': { content: 'none' },
              },
            '.MuiDataGrid-row .MuiDataGrid-cell.ebv-highlighted + .MuiDataGrid-cell.ebv-highlighted':
              {
                borderLeftWidth: 0,
                '&::before': { content: 'none' },
              },
            '.MuiDataGrid-cell.ebv-trait-leader': {
              backgroundColor: blue[200],
              border: `1px solid ${theme.palette.divider}`,
              boxSizing: 'border-box',
              ...theme.applyStyles('dark', {
                backgroundColor: alpha(blue[400], 0.2),
              }),
            },
            [`& .${gridClasses.row}:hover .MuiDataGrid-cell.ebv-trait-leader`]:
              {
                backgroundColor: blend(
                  blue[200],
                  theme.palette.action.hover,
                  0.06,
                ),
                ...theme.applyStyles('dark', {
                  backgroundColor: blend(
                    alpha(blue[400], 0.2),
                    theme.palette.action.hover,
                    0.05,
                  ),
                }),
              },
            // Avoid doubled border when adjacent trait-leader cells touch
            '.MuiDataGrid-row .MuiDataGrid-cell.ebv-trait-leader + .MuiDataGrid-cell.ebv-trait-leader':
              {
                borderLeftWidth: 0,
              },
            '.MuiDataGrid-cell': {
              borderWidth: '0px',
              borderBottom: `1px solid ${theme.palette.divider}`,
              fontSize: '13px',
              fontWeight: '400',
              letterSpacing: '0.25px',
              alignContent: 'center',
              margin: 0,
              paddingTop: '0px',
              paddingBottom: '0px',
              paddingLeft: '8px',
              paddingRight: '8px',
              color: 'text.primary',
            },
            '.public-search-ebv-value': {
              color: theme.palette.text.primary,
              fontWeight: 600,
              ...theme.applyStyles('dark', {
                color: white[950],
              }),
            },
            '.public-search-ebv-value.public-search-ebv-red-avg': {
              color: 'var(--public-search-ebv-red-avg-color)',
            },
            '.public-search-ebv-detail': {
              color: theme.palette.text.secondary,
              fontWeight: 600,
              letterSpacing: 0,
              ...theme.applyStyles('dark', {
                color: slate[200],
              }),
            },
            '& .MuiDataGrid-main': {
              boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
              borderBottomLeftRadius: '8px',
              borderBottomRightRadius: '8px',
              overflow: 'hidden',
            },
            [`& .${gridClasses.row}.odd`]: {
              backgroundColor: 'background.paper',
            },
            [`& .${gridClasses.row}:hover`]: {
              backgroundColor: theme.palette.action.hover,
            },
            // Style for EBV average row
            [`& .${gridClasses.row}.ebv-average-row`]: {
              backgroundColor: slate[100],
              fontStyle: 'italic',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: slate[200],
              },
              ...theme.applyStyles('dark', {
                backgroundColor: alpha(theme.palette.common.white, 0.06),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.common.white, 0.1),
                },
              }),
            },
            [`& .${gridClasses.row}.ebv-average-row .${gridClasses.cell}`]: {
              alignItems: 'center',
              overflow: 'hidden',
              lineHeight: 1.15,
              py: 0,
            },
            // EBV column group headers (custom EbvGroupHeader) — align with theme text
            [`& .MuiDataGrid-columnHeader--filledGroup[class*="ebv-group-"] .MuiDataGrid-columnHeaderTitleContainer`]:
              {
                justifyContent: 'center !important',
              },
            [`& .MuiDataGrid-columnHeader--filledGroup[class*="ebv-group-"]`]: {
              '& .MuiDataGrid-columnHeaderGroupTitle': {
                fontWeight: 600,
                fontSize: '12px',
              },
            },
            // Tint each EBV category group header with its category colour, the
            // same way the mating predictor view does. Individual index columns
            // keep their stronger purple (.ebv-highlighted) on top.
            ...(Object.fromEntries(
              Object.entries(categoryColorMap).map(
                ([category, tint]): [string, SystemStyleObject<Theme>] => [
                  `& .MuiDataGrid-columnHeader--filledGroup.ebv-group-${getEbvCategoryClassSuffix(category)}`,
                  {
                    backgroundColor: blend(white[50], tint, 0.48),
                    ...theme.applyStyles('dark', {
                      backgroundColor: blend(slate[800], tint, 0.4),
                    }),
                  },
                ],
              ),
            ) as Record<string, SystemStyleObject<Theme>>),
            // Category-colour theming for non-index EBV columns: tinted column
            // header + subtle tinted cells + a category-coloured block border
            // (left/right, inner borders deduped between adjacent same-category
            // columns). This mirrors the index `.ebv-highlighted` purple block in
            // each category's own colour; indexes keep the stronger purple/bold.
            ...(Object.fromEntries(
              Object.entries(categoryColorMap)
                .filter(([category]) => category !== EBV_CATEGORY.INDEXES)
                .flatMap(
                  ([category, tint]): [string, SystemStyleObject<Theme>][] => {
                    const cls = `ebv-cat-${getEbvCategoryClassSuffix(category)}`;
                    const border = categoryBorderColor(tint, false);
                    const borderDark = categoryBorderColor(tint, true);
                    return [
                      [
                        `& .MuiDataGrid-columnHeader.${cls}`,
                        {
                          backgroundColor: categoryHeaderBackground(
                            tint,
                            white[50],
                            false,
                          ),
                          borderTop: `2px solid ${border}`,
                          borderLeft: `2px solid ${border}`,
                          borderRight: `2px solid ${border}`,
                          boxSizing: 'border-box',
                          ...theme.applyStyles('dark', {
                            backgroundColor: categoryHeaderBackground(
                              tint,
                              slate[800],
                              true,
                            ),
                            borderTopColor: borderDark,
                            borderLeftColor: borderDark,
                            borderRightColor: borderDark,
                          }),
                        },
                      ],
                      [
                        `& .MuiDataGrid-cell.${cls}`,
                        {
                          backgroundColor: categoryCellBackground(
                            tint,
                            white[50],
                            false,
                          ),
                          borderLeft: `2px solid ${border}`,
                          borderRight: `2px solid ${border}`,
                          boxSizing: 'border-box',
                          ...theme.applyStyles('dark', {
                            backgroundColor: categoryCellBackground(
                              tint,
                              slate[800],
                              true,
                            ),
                            borderLeftColor: borderDark,
                            borderRightColor: borderDark,
                          }),
                        },
                      ],
                      [
                        `& .${gridClasses.row}:hover .MuiDataGrid-cell.${cls}`,
                        {
                          backgroundColor: categoryCellHoverBackground(
                            tint,
                            white[50],
                            theme.palette.action.hover,
                            false,
                          ),
                          ...theme.applyStyles('dark', {
                            backgroundColor: categoryCellHoverBackground(
                              tint,
                              slate[800],
                              theme.palette.action.hover,
                              true,
                            ),
                          }),
                        },
                      ],
                      // Drop the inner left border between adjacent same-category
                      // columns so each category reads as one continuous block.
                      [
                        `& .MuiDataGrid-columnHeader.${cls} + .MuiDataGrid-columnHeader.${cls}`,
                        { borderLeftWidth: 0 },
                      ],
                      [
                        `& .MuiDataGrid-row .MuiDataGrid-cell.${cls} + .MuiDataGrid-cell.${cls}`,
                        { borderLeftWidth: 0 },
                      ],
                    ];
                  },
                ),
            ) as Record<string, SystemStyleObject<Theme>>),
            ...(hasEbvColumnGrouping && {
              '& .MuiDataGrid-columnHeaders': {
                '--DataGrid-headersTotalHeight': `${EBV_COLUMN_HEADERS_TOTAL_HEIGHT}px`,
              },
              [`& .MuiDataGrid-columnHeaders > div[role="row"][aria-rowindex="1"]`]:
                {
                  height: `${EBV_GROUP_COLUMN_HEADER_HEIGHT}px !important`,
                  minHeight: `${EBV_GROUP_COLUMN_HEADER_HEIGHT}px !important`,
                  maxHeight: `${EBV_GROUP_COLUMN_HEADER_HEIGHT}px !important`,
                },
              [`& .MuiDataGrid-columnHeaders > div[role="row"][aria-rowindex="1"] .MuiDataGrid-columnHeader`]:
                {
                  height: `${EBV_GROUP_COLUMN_HEADER_HEIGHT}px !important`,
                  minHeight: `${EBV_GROUP_COLUMN_HEADER_HEIGHT}px !important`,
                  maxHeight: `${EBV_GROUP_COLUMN_HEADER_HEIGHT}px !important`,
                },
              [`& .MuiDataGrid-columnHeaders > .${gridClasses['row--borderBottom']}`]:
                {
                  height: `${EBV_TRAIT_COLUMN_HEADER_HEIGHT}px !important`,
                  minHeight: `${EBV_TRAIT_COLUMN_HEADER_HEIGHT}px !important`,
                  maxHeight: `${EBV_TRAIT_COLUMN_HEADER_HEIGHT}px !important`,
                },
              [`& .MuiDataGrid-columnHeaders > .${gridClasses['row--borderBottom']} .MuiDataGrid-columnHeader`]:
                {
                  minHeight: `${EBV_TRAIT_COLUMN_HEADER_HEIGHT}px`,
                  height: `${EBV_TRAIT_COLUMN_HEADER_HEIGHT}px`,
                },
              [`& .MuiDataGrid-columnHeaders > .${gridClasses['row--borderBottom']} .MuiDataGrid-columnHeader:not(.ebv-column)`]:
                {
                  alignItems: 'flex-end',
                  pb: '4px',
                  [`& .${gridClasses.columnHeaderDraggableContainer}`]: {
                    height: '100%',
                    alignItems: 'flex-end',
                  },
                  [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                    alignItems: 'flex-end',
                  },
                  [`& .${gridClasses.columnSeparator}`]: {
                    top: 0,
                    bottom: 0,
                    height: '100% !important',
                    minHeight: 'unset !important',
                    justifyContent: 'flex-end',
                    pb: '4px',
                  },
                },
              // DataGrid Pro appends an internal flex Filler after header rows; do not
              // let it inherit header-row height (was matched by div:last-of-type).
              '& .MuiDataGrid-columnHeaders > div:last-child:not([role="row"])':
                {
                  flex: '0 0 0px',
                  height: 0,
                  minHeight: 0,
                  overflow: 'hidden',
                },
            }),
          }),
          ...toDataGridSxArray(sxFromProps),
        ]}
      />
      {showPagination && paginationModel && (
        <Box sx={{ flexShrink: 0, display: 'flex', pt: { xs: 0.5, sm: 1.5 } }}>
          <Pagination
            page={paginationModel.page}
            pageCount={Math.ceil(rowCount / paginationModel.pageSize)}
            pageSize={paginationModel.pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            rowCount={rowCount}
            onPageChange={(page) =>
              handlePaginationModelChange({ ...paginationModel, page })
            }
            onPageSizeChange={(pageSize) =>
              handlePaginationModelChange({ page: 0, pageSize })
            }
          />
        </Box>
      )}
    </div>
  );
}
